/**
 * Track color ramp and the "Filter by color / speed" display filter, shared by
 * the Tracks screen (Mapbox) and the Fields screen tracks layer (OpenLayers).
 *
 * Deliberately free of map-library imports so either screen can load it
 * without pulling in the other's renderer.
 */
import type { TrackPointKeep } from "@screens/TracksScreen/utils";
import { DEFAULT_MIN_TURN_RADIUS_M } from "@screens/TracksScreen/turnRadius";

/**
 * The sensor ramp, transcribed from TracksScreen's SENSOR_VALUE_FILL (Screen.tsx).
 *
 * Mapbox interpolates that stop list on the GPU; OpenLayers has no equivalent,
 * so it is evaluated here. Kept as the same stops in the same order so the two
 * screens are visually identical — a field's swath must not change color when
 * the user opens it from the other screen.
 */
const SENSOR_STOPS: Array<[number, [number, number, number]]> = [
  [0.0, [165, 0, 38]],
  [5.0, [215, 48, 39]],
  [10.0, [244, 109, 67]],
  [15.0, [253, 174, 97]],
  [20.0, [254, 224, 139]],
  [35.0, [255, 255, 191]],
  [45.0, [217, 239, 139]],
  [55.0, [166, 217, 106]],
  [62.5, [102, 189, 99]],
  [80.0, [26, 152, 80]],
  [90.0, [0, 104, 55]],
  [99.0, [37, 37, 37]],
];

export function sensorValueColor(value: number): string {
  const v = Number.isFinite(value) ? value : 0;
  if (v <= SENSOR_STOPS[0][0]) return rgb(SENSOR_STOPS[0][1]);
  const last = SENSOR_STOPS[SENSOR_STOPS.length - 1];
  if (v >= last[0]) return rgb(last[1]);

  for (let i = 1; i < SENSOR_STOPS.length; i++) {
    const [hi, hiColor] = SENSOR_STOPS[i];
    if (v > hi) continue;
    const [lo, loColor] = SENSOR_STOPS[i - 1];
    const t = (v - lo) / (hi - lo || 1);
    return rgb([
      Math.round(loColor[0] + (hiColor[0] - loColor[0]) * t),
      Math.round(loColor[1] + (hiColor[1] - loColor[1]) * t),
      Math.round(loColor[2] + (hiColor[2] - loColor[2]) * t),
    ]);
  }
  return rgb(last[1]);
}

const rgb = (c: [number, number, number]) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

/** CSS gradient of the sensor ramp across 0–100, left to right — the track
 *  for the "Filter by color" range slider. */
export const SENSOR_RAMP_GRADIENT = `linear-gradient(to right, ${SENSOR_STOPS.map(
  ([v, c]) => `${rgb(c)} ${Math.min(v, 100)}%`
).join(", ")})`;

/**
 * Coarse color bands on the same 0–100 sensor scale the swaths are colored
 * with (`Math.floor(norm * 100)`). The filter itself is a continuous range now;
 * these remain as the legend swatches (and the backend's KML dot colors).
 */
export const SENSOR_BANDS: ReadonlyArray<{ lo: number; hi: number }> = [
  { lo: 0, hi: 10 },
  { lo: 10, hi: 20 },
  { lo: 20, hi: 35 },
  { lo: 35, hi: 55 },
  { lo: 55, hi: 80 },
  { lo: 80, hi: 101 },
];

/** Swatch for a band: the ramp at the band's midpoint. */
export const sensorBandColor = (band: { lo: number; hi: number }): string =>
  sensorValueColor((band.lo + Math.min(band.hi, 100)) / 2);

/** Ends of the 0–100 sensor scale the color range slider spans. */
export const SENSOR_SCALE_MIN = 0;
export const SENSOR_SCALE_MAX = 100;

/**
 * Display filter for the track maps. Speeds are km/h whatever unit the user
 * reads them in; null means "no bound".
 */
export type TTrackFilter = {
  /** Sensor values kept, inclusive, on the 0–100 scale; null = no bound. Only
   *  the sensor metric is filtered by color — on any other metric the colors
   *  mean nothing. */
  sensorMin: number | null;
  sensorMax: number | null;
  speedMinKmh: number | null;
  speedMaxKmh: number | null;
  /** Hide headland turns: fixes turning tighter than the device's analytics
   *  `min_turn_radius_m`. Applies to every metric. */
  removeTurns: boolean;
};

/** A color range that keeps the whole scale filters nothing. */
const colorRangeActive = (f: TTrackFilter): boolean =>
  (f.sensorMin != null && f.sensorMin > SENSOR_SCALE_MIN) ||
  (f.sensorMax != null && f.sensorMax < SENSOR_SCALE_MAX);

/**
 * `colorApplies`: whether the drawn metric is the sensor value. Only then does
 * the color range mean anything; speed applies to every metric.
 */
export const isTrackFilterActive = (
  f: TTrackFilter | null | undefined,
  colorApplies: boolean
): boolean =>
  !!f &&
  ((colorApplies && colorRangeActive(f)) ||
    f.speedMinKmh != null ||
    f.speedMaxKmh != null ||
    f.removeTurns);

/**
 * The sensor ranges the color filter hides, as the export endpoints take them:
 * `lo` inclusive, `hi` exclusive, on the 0–100 scale. Sensor values are whole
 * numbers, so keeping [min, max] hides [0, min) and [max + 1, 101).
 */
export function hiddenSensorRanges(
  f: TTrackFilter | null | undefined
): Array<{ lo: number; hi: number }> {
  if (!f || !colorRangeActive(f)) return [];
  const out: Array<{ lo: number; hi: number }> = [];
  if (f.sensorMin != null && f.sensorMin > SENSOR_SCALE_MIN) {
    out.push({ lo: SENSOR_SCALE_MIN, hi: f.sensorMin });
  }
  if (f.sensorMax != null && f.sensorMax < SENSOR_SCALE_MAX) {
    out.push({ lo: f.sensorMax + 1, hi: SENSOR_SCALE_MAX + 1 });
  }
  return out;
}

/**
 * The keep-predicate the swath builders take, or undefined when nothing is
 * filtered (so the unfiltered path stays byte-for-byte what it was).
 *
 * Built per machine: `minTurnRadiusM` is that device's analytics turn gate
 * (null/undefined = the analytics default; 0 = gate off, turns never hidden).
 */
export function trackKeepFor(
  filter: TTrackFilter | null | undefined,
  colorApplies: boolean,
  minTurnRadiusM?: number | null
): TrackPointKeep | undefined {
  if (!isTrackFilterActive(filter, colorApplies)) return undefined;
  const turnGate = filter!.removeTurns ? minTurnRadiusM ?? DEFAULT_MIN_TURN_RADIUS_M : 0;
  const color = colorApplies && colorRangeActive(filter!);
  const lo = filter!.sensorMin ?? SENSOR_SCALE_MIN;
  const hi = filter!.sensorMax ?? SENSOR_SCALE_MAX;
  const min = filter!.speedMinKmh;
  const max = filter!.speedMaxKmh;
  const keep: TrackPointKeep = (sensor, speedKmh, turnRadiusM) => {
    // Unknown radius (null) keeps the fix, as in analytics.
    if (turnGate > 0 && turnRadiusM != null && turnRadiusM < turnGate) return false;
    if (min != null && speedKmh < min) return false;
    if (max != null && speedKmh > max) return false;
    if (color && sensor != null) {
      const v = Math.min(Math.max(Math.floor(sensor * 100), SENSOR_SCALE_MIN), SENSOR_SCALE_MAX);
      if (v < lo || v > hi) return false;
    }
    return true;
  };
  keep.removesTurns = turnGate > 0;
  return keep;
}
